import React from 'react';
import Link from 'next/link';
import round from 'lodash/round';
import floor from 'lodash/floor';
import cloneDeep from 'lodash/cloneDeep';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import GeneralChart from '@/components/job-title/GeneralChart';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { renameL1Name } from '@/components/job-ranking/common';
import { JobTitleContext } from '@/context/create-context';
import { SALARY_RANGES, TAB_SALARY_AND_WELFARE } from '@/constants/job-title';
import { JOB_RANKING_PAGE_ROUTER_PATH } from '@/constants/router-path';
import { trackClickEvent } from '@/utils/site-track';

type Props = {
  footer?: React.ReactNode;
};

const SalaryBenefitsCard = ({ footer }: Props) => {
  const context = React.useContext(JobTitleContext);
  const {
    jobTitle: {
      city_name: cityName = '',
      salary_details: { avg, distribution_details: distributionDetails },
      location_salary,
      salary_rank: salaryRank,
      id: jobTitleId,
    },
    displayName,
  } = context;
  const lineChartRef = React.useRef(null);
  const barChartRef = React.useRef(null);

  const initDistributionDetails = React.useMemo(() => {
    const totalCount = Object.values(distributionDetails).reduce(
      (a, b) => a + b,
      0,
    );
    const data = cloneDeep(distributionDetails);
    SALARY_RANGES.map((item) => {
      if (!data[item]) data[item] = 0;
    });
    return {
      data,
      totalCount,
    };
  }, []);

  const seoValue = React.useMemo(() => {
    const { data, totalCount } = initDistributionDetails;
    const newData = cloneDeep(data);
    const sortIndexData = Object.keys(newData)
      .filter((key) => Reflect.get(newData, key) > 0)
      .sort((a, b) => SALARY_RANGES.indexOf(a) - SALARY_RANGES.indexOf(b));
    const salaryDate = cloneDeep(sortIndexData).sort(
      (a, b) => Reflect.get(newData, b) - Reflect.get(newData, a),
    );
    const mostSalaryData = salaryDate[0];
    const minSalaryData = salaryDate[salaryDate.length - 1];
    const minSalaryRate = floor(
      (Reflect.get(newData, minSalaryData) / totalCount) * 100,
      1,
    );
    const salaryRangeData = () => {
      const minKey = sortIndexData[0];
      if (sortIndexData.length > 1) {
        const maxKey = sortIndexData[sortIndexData.length - 1];
        return (
          minKey.split('-')[0] +
          'k至' +
          (maxKey.includes('-') ? maxKey.split('-')[1] : '30k以上')
        );
      }
      return minKey.includes('-') ? minKey.replace('-', 'k至') : '30k以上';
    };

    return {
      salaryRange: salaryRangeData(),
      mostSalaryRange: mostSalaryData.includes('-')
        ? mostSalaryData.replace('-', 'k至')
        : '30k以上',
      mostSalaryRate:
        floor((Reflect.get(newData, mostSalaryData) / totalCount) * 100, 1) +
        '%',
      minSalaryRange: minSalaryData.includes('-')
        ? minSalaryData.replace('-', 'k至')
        : '30k以上',
      minSalaryDegree:
        minSalaryRate > 60 ? '大部分' : minSalaryRate < 35 ? '小部分' : '适中',
      minSalaryData: minSalaryRate + '%',
    };
  }, []);

  const {
    mostSalaryRange,
    mostSalaryRate,
    minSalaryRange,
    minSalaryDegree,
    minSalaryData,
  } = seoValue;

  const lineConfig = React.useMemo<ECOption>(() => {
    const ranks = salaryRank.ranks;
    const nameData = ranks.map((item) => item.name);
    const avgSalary = ranks.map(({ id, avg }) => {
      if (id === jobTitleId) {
        return {
          value: round(avg, -2),
          label: {
            show: true,
            formatter: () => `第${salaryRank.current_rank}`,
          },
        };
      }
      return {
        value: round(avg, -2),
      };
    });

    return {
      grid: {
        show: false,
        containLabel: true,
        left: 0,
        bottom: '2%',
        top: '30%',
      },
      xAxis: {
        type: 'category',
        data: nameData,
        axisTick: { show: false },
        axisLine: { show: false },
        boundaryGap: false,
        axisLabel: {
          fontSize: 10,
          interval: 0,
          color: 'rgba(0,0,0,0.6)',
          overflow: 'truncate',
          width: 65,
        },
      },
      yAxis: {
        type: 'value',
        show: false,
        min: 'dataMin',
        max: 'dataMax',
        minInterval: 100,
      },
      tooltip: {
        show: false,
      },
      series: [
        {
          type: 'line',
          data: avgSalary,
          smooth: true,
          cursor: 'default',
          symbol: 'circle',
          symbolSize: 6,
          triggerLineEvent: false,
          lineStyle: {
            color: 'rgba(234,88,12,0.5)',
            width: 3,
          },
          itemStyle: { color: 'rgba(234,88,12)' },
          legendHoverLink: false,
          emphasis: { disabled: true },
          label: {
            show: false,
            fontSize: 12,
            color: 'rgba(0,0,0,0.85)',
            backgroundColor: 'transparent',
          },
        },
      ],
    };
  }, []);

  const barConfig = React.useMemo<ECOption>(() => {
    const { data, totalCount } = initDistributionDetails;
    const newData = cloneDeep(data);
    const barData = Object.keys(newData)
      .sort((a, b) => SALARY_RANGES.indexOf(a) - SALARY_RANGES.indexOf(b))
      .reduce((acc: { value: number; category: string }[], cur) => {
        const value = Number(
          ((Reflect.get(newData, cur) / totalCount) * 100).toFixed(1),
        );
        acc.push({ category: cur, value: value > 0 ? value : 0 });
        return acc;
      }, [])
      .sort(
        (a, b) =>
          SALARY_RANGES.indexOf(b.category) - SALARY_RANGES.indexOf(a.category),
      );

    return {
      grid: { show: false, top: 0, bottom: 0, left: 60, right: 55 },
      xAxis: {
        type: 'value',
        min: '0',
        max: '100',
        show: false,
      },
      yAxis: [
        {
          type: 'category',
          offset: 50,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: 'rgba(0, 0, 0,0.85)',
            fontSize: 13,
            fontWeight: 600,
            align: 'left',
          },
          data: barData.map((item) => item.category),
        },
        {
          type: 'category',
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            formatter: (value: string) => `${value}%`,
            color: '#979797',
            fontSize: 13,
          },
          data: barData.map((item) => item.value),
        },
      ],
      series: [
        {
          type: 'bar',
          data: barData,
          showBackground: true,
          backgroundStyle: {
            color: '#eee',
            borderRadius: 8,
          },
          barWidth: 10,
          cursor: 'default',
          itemStyle: {
            color: 'rgba(0, 132, 137, 0.64)',
            borderRadius: 8,
          },
        },
      ],
    };
  }, []);

  return (
    <GeneralChart
      type="custom"
      title={`${cityName}${displayName}收入怎么样？`}
      footer={footer}
    >
      <div className="tablet:grid tablet:grid-cols-[calc(45%-1px)_2px_calc(55%-1px)]">
        <div className="py-6">
          <div className="px-5 tablet:px-12">
            <p className="font-medium text-base">平均月薪</p>
            <p className="font-bold mt-2 text-4xl text-2.75xl tablet:text-4xl">
              {`¥${round(avg, -2)}`}
            </p>
            <p className="mt-4 text-base leading-5 tablet:text-xs">
              {`排名第${salaryRank.current_rank}`}
            </p>
          </div>
          <div className="h-25 px-5 [&_canvas]:cursor-default tablet:pl-12 tablet:pr-2">
            <CusEcharts chartRef={lineChartRef} config={lineConfig} />
          </div>
          <div className="px-5 tablet:px-12">
            <Link
              className="mt-2 text-xs text-web-blue-500"
              href={`${JOB_RANKING_PAGE_ROUTER_PATH}${
                location_salary?.pinyin ? `${location_salary.pinyin}_` : ''
              }${salaryRank.l1_id}/`}
              onClick={(e) => {
                trackClickEvent(e);
              }}
              data-ele-group={`${TAB_SALARY_AND_WELFARE}_l1-rank`}
              data-ele-id={`l1-rank_${salaryRank.l1_name}`}
            >
              {`查看${cityName}${renameL1Name(
                salaryRank.l1_id,
                salaryRank.l1_name,
              )}全部排名`}
            </Link>
            <DataInterpretationSEO dataEleGroup={TAB_SALARY_AND_WELFARE}>
              {`${cityName}${displayName}一个月多少钱？平均工资为¥${round(
                avg,
                -2,
              )}，其中收入在${mostSalaryRange}的人群占比最多，达${mostSalaryRate}; 收入在${minSalaryRange}的人群占比${minSalaryDegree}，达${minSalaryData}。本数据统计依赖于各大平台发布的公开薪酬，仅供参考。`}
            </DataInterpretationSEO>
          </div>
        </div>
        <div className="w-full h-px bg-white-smoke tablet:h-full tablet:w-0.5 tablet:m-0" />
        <div className="py-6 tablet:px-12">
          <span className="font-medium px-5 text-base tablet:text-sm tablet:p-0">
            月薪分布
          </span>
          <div className="h-36 mt-5 px-5 tablet:h-52 tablet:mt-4 tablet:p-0">
            <CusEcharts chartRef={barChartRef} config={barConfig} />
          </div>
        </div>
      </div>
    </GeneralChart>
  );
};

export default SalaryBenefitsCard;
